import * as React from 'react'
import { AlertTriangle, Check, Copy, KeyRound, Pencil, Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { toolsService } from '@/services/toolsService'
import type { WorkspaceSecret } from '@/types/tool'
import { PageHeader } from '@/components/ui/page-header'

export function KeysSecretsPage() {
  const { t } = useTranslation()
  const [secrets, setSecrets] = React.useState<WorkspaceSecret[]>([])
  const [loading, setLoading] = React.useState(false)
  const [open, setOpen] = React.useState(false)
  const [editing, setEditing] = React.useState<WorkspaceSecret | null>(null)
  const [createStep, setCreateStep] = React.useState<'form' | 'reveal'>('form')
  const [revealedSecretValue, setRevealedSecretValue] = React.useState('')
  const [copied, setCopied] = React.useState(false)
  const [name, setName] = React.useState('')
  const [value, setValue] = React.useState('')
  const [description, setDescription] = React.useState('')
  const [error, setError] = React.useState<string | null>(null)
  // Borrar un secreto no se puede deshacer: su valor no se vuelve a mostrar
  // nunca, asi que no hay de donde sacarlo otra vez. Era un clic.
  const [porBorrar, setPorBorrar] = React.useState<WorkspaceSecret | null>(null)

  const resetDialog = React.useCallback(() => {
    setEditing(null)
    setCreateStep('form')
    setRevealedSecretValue('')
    setCopied(false)
    setName('')
    setValue('')
    setDescription('')
    setError(null)
  }, [])

  const loadSecrets = React.useCallback(async () => {
    setLoading(true)
    try {
      const items = await toolsService.listSecrets()
      setSecrets([...items].sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime()))
      setError(null)
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : t('keys_secrets.errors.generic'))
    } finally {
      setLoading(false)
    }
  }, [t])

  React.useEffect(() => {
    void loadSecrets()
  }, [loadSecrets])

  const startCreate = () => {
    resetDialog()
    setOpen(true)
  }

  const startEdit = (secret: WorkspaceSecret) => {
    setEditing(secret)
    setCreateStep('form')
    setRevealedSecretValue('')
    setCopied(false)
    setName(secret.name)
    setValue('')
    setDescription(secret.description || '')
    setError(null)
    setOpen(true)
  }

  const handleSave = async () => {
    setLoading(true)
    try {
      if (editing) {
        await toolsService.updateSecret(editing.id, {
          name: name.trim(),
          description: description.trim() || '',
          ...(value.trim() ? { secret_value: value.trim() } : {}),
        })
        setOpen(false)
        await loadSecrets()
      } else {
        await toolsService.createSecret({
          name: name.trim(),
          description: description.trim() || undefined,
          secret_value: value.trim(),
          is_enabled: true,
        })
        await loadSecrets()
        setRevealedSecretValue(value.trim())
        setCreateStep('reveal')
        setValue('')
      }
      setError(null)
    } catch (saveError) {
      setError(saveError instanceof Error ? saveError.message : t('keys_secrets.errors.generic'))
    } finally {
      setLoading(false)
    }
  }

  const handleCopySecret = async () => {
    try {
      await navigator.clipboard.writeText(revealedSecretValue)
      setCopied(true)
    } catch {
      setError(t('keys_secrets.errors.generic'))
    }
  }

  const handleDelete = async () => {
    if (!porBorrar) return
    const secretId = porBorrar.id
    setPorBorrar(null)
    setLoading(true)
    try {
      await toolsService.deleteSecret(secretId)
      await loadSecrets()
    } catch (deleteError) {
      setError(deleteError instanceof Error ? deleteError.message : t('keys_secrets.errors.generic'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="container mx-auto space-y-8 p-3 md:p-6">
      <section>
        <PageHeader title={t('keys_secrets.title')} description={t('keys_secrets.subtitle')}>
          <Button size="lg" onClick={startCreate} disabled={loading}>
            <Plus className="mr-2 h-4 w-4" />
            {t('keys_secrets.create')}
          </Button>
        </PageHeader>
      </section>

      {secrets.length === 0 ? (
        <Alert>
          <AlertTitle>{t('keys_secrets.empty_title')}</AlertTitle>
          <AlertDescription>{t('keys_secrets.empty_description')}</AlertDescription>
        </Alert>
      ) : (
        <div className="space-y-2">
          {secrets.map((secret) => (
            <div key={secret.id} className="flex items-start justify-between gap-4 rounded-lg border bg-card/50 p-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <KeyRound className="h-4 w-4 text-muted-foreground" />
                  <p className="truncate font-medium">{secret.name}</p>
                </div>
                <p className="mt-1 text-xs text-muted-foreground">Valor oculto por seguridad. No se vuelve a mostrar.</p>
                {secret.description && <p className="mt-1 text-xs text-muted-foreground">{secret.description}</p>}
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={() => startEdit(secret)} disabled={loading}>
                  <Pencil className="mr-1 h-4 w-4" />
                  {t('keys_secrets.edit')}
                </Button>
                <Button variant="ghost" size="sm" className="text-destructive hover:text-destructive" onClick={() => setPorBorrar(secret)} disabled={loading}>
                  <Trash2 className="mr-1 h-4 w-4" />
                  {t('keys_secrets.delete')}
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog
        open={open}
        onOpenChange={(nextOpen) => {
          setOpen(nextOpen)
          if (!nextOpen) resetDialog()
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {editing
                ? t('keys_secrets.edit_title')
                : createStep === 'form'
                  ? t('keys_secrets.create_title')
                  : 'Secreto creado'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            {!editing && createStep === 'reveal' ? (
              <>
                <Alert className="border-warning/30 bg-warning/10">
                  <AlertTriangle className="h-4 w-4 text-warning" />
                  <AlertTitle>Copia y guardalo en un lugar seguro</AlertTitle>
                  <AlertDescription>
                    Una vez cierres este cuadro no volvera a mostrarse el valor del secreto.
                  </AlertDescription>
                </Alert>
                <div className="space-y-2">
                  <Label>Valor del secreto</Label>
                  <Textarea value={revealedSecretValue} readOnly rows={3} className="font-mono" />
                </div>
              </>
            ) : (
              <>
                <Alert className="border-warning/30 bg-warning/10">
                  <AlertTriangle className="h-4 w-4 text-warning" />
                  <AlertTitle>Atencion</AlertTitle>
                  <AlertDescription>
                    {editing
                      ? 'El valor actual del secreto no se muestra por seguridad. Si escribes un nuevo valor, reemplazara al anterior.'
                      : 'El valor del secreto se mostrara solo una vez despues de crearlo.'}
                  </AlertDescription>
                </Alert>
                <div className="space-y-2">
                  <Label htmlFor="secret-name">{t('keys_secrets.name')} *</Label>
                  <Input id="secret-name" value={name} onChange={(event) => setName(event.target.value)} placeholder="OPENAI_API_KEY" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secret-value">{editing ? 'Nuevo valor (opcional)' : `${t('keys_secrets.value')} *`}</Label>
                  <Input id="secret-value" value={value} onChange={(event) => setValue(event.target.value)} placeholder={editing ? 'Dejar vacio para mantener el actual' : 'sk-...'} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="secret-description">{t('keys_secrets.description')}</Label>
                  <Textarea id="secret-description" value={description} onChange={(event) => setDescription(event.target.value)} rows={2} />
                </div>
              </>
            )}
            {error && <p className="text-sm text-destructive">{error}</p>}
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setOpen(false)}>
              {createStep === 'reveal' ? t('common.close') : t('keys_secrets.cancel')}
            </Button>
            {!editing && createStep === 'reveal' ? (
              <Button type="button" onClick={handleCopySecret}>
                {copied ? <Check className="mr-1 h-4 w-4" /> : <Copy className="mr-1 h-4 w-4" />}
                {copied ? 'Copiado' : 'Copiar valor'}
              </Button>
            ) : (
              <Button type="button" onClick={() => void handleSave()} disabled={loading}>
                {editing ? t('keys_secrets.save') : t('keys_secrets.create')}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={porBorrar !== null} onOpenChange={(abierto) => !abierto && setPorBorrar(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('keys_secrets.delete_title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('keys_secrets.delete_description', { name: porBorrar?.name ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('keys_secrets.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => void handleDelete()}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {t('keys_secrets.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
